import React from 'react';
import {
    CalendarCancel20Regular,
    ClipboardError20Regular,
    Wrench20Regular,
    Warning20Regular,
    LightbulbFilament20Regular,
    DocumentSearch20Regular,
} from '@fluentui/react-icons';

import { SyncAnalysisFinding } from '../../../../models/Entities/Protocols/SyncAnalysis';
import { SyncAnalysisFindingTypeEnum } from '../../../../models/Enums/SyncAnalysisFindingTypeEnum';
import * as ComponentFunctions from './ConsumptionSyncProcessIAAnalysis.Functions';

const TYPE_ICONS: Record<SyncAnalysisFindingTypeEnum, React.ReactNode> = {
    [SyncAnalysisFindingTypeEnum.VisitWithoutConsumption]: <CalendarCancel20Regular />,
    [SyncAnalysisFindingTypeEnum.UnsettledFormItem]: <ClipboardError20Regular />,
    [SyncAnalysisFindingTypeEnum.MisconfiguredConcept]: <Wrench20Regular />,
    [SyncAnalysisFindingTypeEnum.ContradictoryConfiguration]: <Warning20Regular />,
};

interface IProps {
    finding: SyncAnalysisFinding;
}

/**
 * Un hallazgo. Es sólo lectura: la resolución de hallazgos no está en uso, así
 * que la tarjeta no ofrece acciones ni muestra decisión.
 */
export const SyncAnalysisFindingCard = ({ finding }: IProps) => {
    const descriptor = ComponentFunctions.getFindingTypeDescriptor(finding.findingType);
    const typeLabel = ComponentFunctions.getFindingTypeLabel(finding.findingType);
    const typeClass = 'csa-finding--type' + finding.findingType;

    // La confianza llega como entero porcentual; se acota por si el servidor
    // devolviera algo fuera de rango, para que la barra no se desborde.
    const confidence = Math.max(0, Math.min(100, finding.confidence ?? 0));

    const references = [
        finding.protocolVisitEntryId !== null && finding.protocolVisitEntryId !== undefined
            ? { label: 'Visita', value: '#' + finding.protocolVisitEntryId }
            : null,
        finding.contractConceptId !== null && finding.contractConceptId !== undefined
            ? { label: 'Concepto', value: '#' + finding.contractConceptId }
            : null,
        finding.recommendedContractConceptId !== null && finding.recommendedContractConceptId !== undefined
            ? { label: 'Concepto sugerido', value: '#' + finding.recommendedContractConceptId }
            : null,
        finding.formItemDescription ? { label: 'Ítem', value: finding.formItemDescription } : null,
    ].filter(Boolean) as { label: string; value: string }[];

    return (
        <li className={'csa-finding ' + typeClass}>
            <div className="csa-finding__head">
                <span className="csa-finding__icon">
                    {TYPE_ICONS[finding.findingType] ?? <DocumentSearch20Regular />}
                </span>
                <div className="csa-finding__headText">
                    <span className="csa-finding__type" title={descriptor?.description}>
                        {typeLabel}
                    </span>
                    <span className="csa-finding__subject">
                        {finding.subjectDescription || descriptor?.anchor || '—'}
                    </span>
                </div>
                <div className="csa-finding__flags">
                    {finding.isNew && <span className="csa-tag csa-tag--new">Nuevo</span>}
                    {finding.reopened && <span className="csa-tag csa-tag--reopened">Reabierto</span>}
                </div>
            </div>

            <div className="csa-finding__scores">
                <span className="csa-finding__severity" title="Prioridad asignada por el análisis">
                    Severidad {finding.severity}
                </span>
                <span className="csa-finding__confidence" title="Confianza del análisis sobre este hallazgo">
                    <span className="csa-finding__confidenceBar">
                        <span
                            className="csa-finding__confidenceFill"
                            style={{ width: confidence + '%' }}
                        />
                    </span>
                    {confidence}%
                </span>
            </div>

            {references.length > 0 && (
                <dl className="csa-finding__refs">
                    {references.map(reference => (
                        <div className="csa-finding__ref" key={reference.label}>
                            <dt>{reference.label}</dt>
                            <dd>{reference.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {finding.evidence && (
                <div className="csa-finding__block">
                    <span className="csa-finding__blockTitle">
                        <DocumentSearch20Regular />
                        Evidencia
                    </span>
                    <p className="csa-finding__blockText">{finding.evidence}</p>
                </div>
            )}

            {finding.recommendation && (
                <div className="csa-finding__block csa-finding__block--recommendation">
                    <span className="csa-finding__blockTitle">
                        <LightbulbFilament20Regular />
                        Recomendación
                    </span>
                    <p className="csa-finding__blockText">{finding.recommendation}</p>
                </div>
            )}
        </li>
    );
};

export default SyncAnalysisFindingCard;
